import { lens, withLenses } from "@dhmk/zustand-lens";
import type { StateCreator } from "zustand";
import type { ConsoleLogMessage } from "@imagine/sandbox-instrumentation";

const MAX_LOG_ENTRIES = 200;

type ConsoleLogsSliceState = {
  logs: Array<ConsoleLogMessage>;
};

type ConsoleLogsSliceActions = {
  appendLog: (log: ConsoleLogMessage) => void;
  mergeHistory: (history: Array<ConsoleLogMessage>) => void;
  reset: () => void;
};

export type ConsoleLogsSlice = ConsoleLogsSliceState & ConsoleLogsSliceActions;

const sortAndLimitLogs = (
  logs: Array<ConsoleLogMessage>,
): Array<ConsoleLogMessage> => {
  return [...logs]
    .sort(
      (a, b) =>
        new Date(a.logged_at).getTime() - new Date(b.logged_at).getTime(),
    )
    .slice(-MAX_LOG_ENTRIES);
};

const mergeLogs = (
  existing: Array<ConsoleLogMessage>,
  additions: Array<ConsoleLogMessage>,
): Array<ConsoleLogMessage> => {
  if (!additions.length) return existing;
  const logsById = new Map<string, ConsoleLogMessage>();
  for (const log of [...existing, ...additions]) {
    logsById.set(log.id, log);
  }
  return sortAndLimitLogs(Array.from(logsById.values()));
};

export const consoleLogsSlice = lens<ConsoleLogsSlice>((set, get, api) => ({
  logs: [],
  appendLog: (log) => set({ logs: mergeLogs(get().logs, [log]) }),
  mergeHistory: (history) => set({ logs: mergeLogs(get().logs, history) }),
  reset: () => set({ logs: [] }),
}));
